import { useStore } from 'zustand';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ImportReviewStoreApi } from '../../lib/state/importReviewStore';

type RuleSummary = {
  pattern: string;
  categoryLabel: string;
  confidence: number;
};

interface RuleSuggestionRowProps {
  store: ImportReviewStoreApi;
  rules: RuleSummary[];
}

function confidenceBadge(confidence: number) {
  if (confidence >= 80) return 'bg-positive-soft text-positive';
  if (confidence >= 50) return 'bg-warning-soft text-warning';
  return 'bg-muted text-muted-foreground';
}

export function RuleSuggestionRow({ store, rules }: RuleSuggestionRowProps) {
  const notice = useStore(store, (state) => state.ruleApplicationNotice);

  if (rules.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-2xl border border-border bg-card">
      <header className="px-4 pt-4 pb-3 sm:px-5">
        <h2 className="text-[15px] font-semibold leading-6">Regler som skapas</h2>
        <p className="text-sm text-muted-foreground">
          Dessa kategoriregler skapas automatiskt vid bekräftelse.
        </p>
      </header>
      {notice && (
        <div className="bg-positive-soft px-4 py-3 text-sm text-positive sm:px-5">
          {notice.appliedCount} ytterligare transaktion
          {notice.appliedCount === 1 ? '' : 'er'} kategoriserades med regeln{' '}
          <span className="font-mono">{notice.pattern}</span>.
        </div>
      )}
      <ul className="divide-y divide-border border-t border-border">
        {rules.map((r) => (
          <li key={r.pattern} className="flex items-center gap-3 px-4 py-2.5 text-sm sm:px-5">
            <span className="min-w-0 flex-1 truncate font-mono text-foreground">{r.pattern}</span>
            <span className="min-w-0 shrink truncate text-muted-foreground">
              → {r.categoryLabel}
            </span>
            <Badge
              variant="outline"
              className={cn(
                'shrink-0 border-transparent tabular-nums',
                confidenceBadge(r.confidence),
              )}
            >
              {r.confidence}%
            </Badge>
          </li>
        ))}
      </ul>
    </section>
  );
}
